import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { POPUP_BLOCKED, openMcpSignIn, prepareMcpSignIn } from './mcpSignIn';
import { useAsync } from '../../pages/aai/useAsync';
import { RemoveMcpConfirm } from './RemoveMcpConfirm';
import type { ConnectorCatalog, McpAuth, McpCatalogEntry, McpServer, McpView, McpWriteLevel } from '../../types/aai';
import { AccessCell } from './connectors/AppAccess';
import {
  Action, Chip, Divided, EmptyState, ErrorNote, Field, FieldGrid, Input, Kicker, ListRow, Loading, Modal, Panel,
  PanelHead, RouteMark, Select, Toggle,
} from '../aa';

/*
 * MCP servers, the second shape of company tool. Each one sits in the same
 * catalog as the apps, so the Owner turns it off or keeps it the same way.
 * The company connects it once, here.
 */

const AUTH_LABEL: Record<McpAuth, string> = {
  none: 'No sign-in',
  bearer: 'Bearer token',
  header: 'Key in a header',
  oauth: 'Sign in with the provider',
};

const WRITE_LABEL: Record<McpWriteLevel, string> = {
  allowed: 'Allowed without asking',
  approval: 'Ask before each change',
  off: 'Reads only, no changes',
  person: 'Each person decides',
};

const detailOf = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback;

const connectionLabel = (s: McpServer): string => {
  if (s.auth === 'none') return 'No sign-in needed';
  if (s.auth === 'oauth') return s.connected ? 'Signed in for the company' : 'Not signed in yet';
  if (s.connected) return 'Connected for the company';
  return 'Needs a token';
};

const readyLabel = (s: McpServer): string => {
  if (s.connected) return 'Ready';
  if (s.auth === 'oauth') return 'Needs sign-in';
  return 'Needs a token';
};

export const McpServersPanel: React.FC = () => {
  const view = useAsync<McpView>(() => aaiApi.listMcp(), 'Could not load MCP servers.');
  const catalog = useAsync<ConnectorCatalog>(() => aaiApi.getConnectors(), 'Could not load the catalog.');
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  if (view.loading) return <Loading label="Loading MCP servers" />;
  if (view.error || !view.data) return <ErrorNote message={view.error || 'Could not load MCP servers.'} />;
  const data = view.data;
  const enabledOf = (key: string): boolean => {
    const row = (catalog.data?.connectors || []).find((c) => c.slug === key);
    return row ? row.enabled : true;
  };
  const reload = () => {
    view.reload();
    catalog.reload();
  };
  const current = data.servers.find((s) => s.key === open) || null;
  const accessOf = (server: McpServer): React.ReactNode => {
    const row = (catalog.data?.connectors || []).find((c) => c.slug === server.key);
    if (!row || !catalog.data?.can_switch_company) return null;
    return <AccessCell row={row} onChanged={reload} />;
  };

  const addSuggested = async (entry: McpCatalogEntry) => {
    setBusy(entry.slug);
    try {
      const { data: server } = await aaiApi.addSuggestedMcp(entry.slug);
      toast.success(`${entry.label} is in your catalog.`);
      if (server.warning) toast.warn(server.warning);
      reload();
      setOpen(server.key);
    } catch (err) {
      toast.error(detailOf(err, `Could not add ${entry.label}.`));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5">
      <Panel>
        <PanelHead
          kicker="MCP"
          title="MCP servers for the company"
          desc="Connect a server once and everyone can use it in their chats. Turn it off, or keep it to yourself, any time."
          action={data.can_change ? <Action size="sm" onClick={() => setAdding(true)}>Add an MCP server</Action> : undefined}
        />
        {data.servers.length === 0 && (
          <EmptyState title="No MCP servers yet" reason={data.can_change ? 'Add one to share it with everyone.' : 'The Owner adds these.'} />
        )}
        <Divided>
          {data.servers.map((s) => (
            <ListRow
              key={s.key}
              mark={<RouteMark glyph={s.label.charAt(0).toUpperCase()} size="lg" />}
              title={s.label}
              desc={<span className="[overflow-wrap:anywhere]">{s.description || s.host}</span>}
              meta={[{ label: 'Sign-in', value: AUTH_LABEL[s.auth] }, { label: 'Changes', value: WRITE_LABEL[s.write_level] }, { label: 'Company', value: enabledOf(s.key) ? 'On' : 'Off' }]}
              metaWidth={[110, 170, 80]}
              right={<span className="flex w-[112px] justify-end"><Chip tone={s.connected ? 'ok' : 'warn'}>{readyLabel(s)}</Chip></span>}
              onClick={() => setOpen(s.key)}
              arrow
            />
          ))}
        </Divided>
      </Panel>

      {data.suggested.length > 0 && (
        <Panel>
          <PanelHead kicker="From Agents Anywhere" title="Known MCP servers" desc="Servers the platform already lists. Add one, then connect it for your company." />
          <Divided>
            {data.suggested.map((e) => (
              <ListRow
                key={e.slug}
                mark={<RouteMark glyph={e.label.charAt(0).toUpperCase()} size="lg" />}
                title={e.label}
                desc={e.description || e.host}
                meta={[{ label: 'Sign-in', value: AUTH_LABEL[e.auth] }]}
                metaWidth={[120]}
                right={data.can_change
                  ? <Action size="sm" variant="outline" disabled={busy === e.slug} onClick={() => addSuggested(e)}>Add</Action>
                  : undefined}
              />
            ))}
          </Divided>
        </Panel>
      )}

      {!data.can_change && (
        <p className="m-0 text-[12.5px] leading-[20px] text-ink-dim">Only whoever turns tools on for the company can add or connect an MCP server. Ask the owner.</p>
      )}

      {adding && <AddMcpDialog onClose={() => setAdding(false)} onAdded={(key) => { setAdding(false); reload(); setOpen(key); }} />}
      {current && (
        <McpServerDialog
          server={current}
          enabled={enabledOf(current.key)}
          canChange={data.can_change}
          canSetLevel={!!catalog.data?.can_manage}
          onClose={() => setOpen(null)}
          onChanged={reload}
          access={accessOf(current)}
        />
      )}
    </div>
  );
};

const McpDialog: React.FC<{
  onClose: () => void;
  kicker: React.ReactNode;
  badge?: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
}> = ({ onClose, kicker, badge, title, sub, footer, children }) => (
  <Modal size="dialog" onClose={onClose} context={kicker} badge={badge} footer={footer}>
    <h2 className="m-0 text-[24px] font-bold leading-[31px] tracking-[-0.015em] text-ink">{title}</h2>
    {sub && <p className="m-0 mt-2 text-[14px] leading-[23px] text-ink-muted">{sub}</p>}
    <div className="mt-6">{children}</div>
  </Modal>
);

const McpServerDialog: React.FC<{
  server: McpServer;
  enabled: boolean;
  canChange: boolean;
  canSetLevel: boolean;
  onClose: () => void;
  onChanged: () => void;
  access: React.ReactNode;
}> = ({ server, enabled, canChange, canSetLevel, onClose, onChanged, access }) => {
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (work: () => Promise<unknown>, done: string, failed: string) => {
    setBusy(true);
    try {
      await work();
      toast.success(done);
      setSecret('');
      onChanged();
    } catch (err) {
      toast.error(detailOf(err, failed));
    } finally {
      setBusy(false);
    }
  };

  const setEnabled = (next: boolean) => run(
    () => aaiApi.setConnectorEnabled(server.key, next),
    next ? `${server.label} is on for the company.` : `${server.label} is off for everyone.`,
    `Could not change ${server.label}.`,
  );
  const setLevel = (level: McpWriteLevel) => run(
    () => aaiApi.setMcpWriteLevel(server.key, level),
    `${server.label}: ${WRITE_LABEL[level].toLowerCase()}.`,
    'That setting did not save.',
  );
  const connect = () => run(async () => {
    const { data } = await aaiApi.connectMcp(server.key, secret);
    if (data.warning) toast.warn(data.warning);
  }, `${server.label} is connected for the company.`, 'That token did not save.');
  const disconnect = () => run(() => aaiApi.disconnectMcp(server.key), `${server.label} is disconnected.`, `Could not disconnect ${server.label}.`);
  const [signingIn, setSigningIn] = useState(false);
  const signIn = async () => {
    const popup = prepareMcpSignIn();
    if (!popup) {
      toast.error(POPUP_BLOCKED);
      return;
    }
    setSigningIn(true);
    try {
      const { data } = await aaiApi.beginMcpOauth(server.key);
      openMcpSignIn(popup, data.authorizeUrl, (ok) => {
        setSigningIn(false);
        if (ok) toast.success(`${server.label} is signed in for the company.`);
        onChanged();
      });
    } catch (err) {
      popup.close();
      setSigningIn(false);
      toast.error(detailOf(err, `Could not start the sign-in for ${server.label}.`));
    }
  };
  const [removing, setRemoving] = useState(false);
  const remove = () => setRemoving(true);
  const confirmRemove = () => {
    run(() => aaiApi.removeMcp(server.key), `${server.label} is out of the catalog.`, `Could not remove ${server.label}.`).then(onClose);
  };

  return (
    <>
    <McpDialog
      onClose={onClose}
      kicker={<Kicker>MCP server</Kicker>}
      badge={<Chip tone={server.connected ? 'ok' : 'warn'}>{readyLabel(server)}</Chip>}
      title={server.label}
      sub={server.description || server.host}
      footer={(
        <div className="flex items-center justify-between gap-3">
          {canChange ? <Action variant="danger" size="md" disabled={busy} onClick={remove}>Remove from catalog</Action> : <span />}
          <Action size="lg" variant="quiet" onClick={onClose}>Close</Action>
        </div>
      )}
    >
      <Panel>
        <Divided>
          <div className="flex items-start justify-between gap-4 px-5 py-4">
            <span>
              <span className="block text-[13.5px] font-semibold text-ink">On for the company</span>
              <span className="mt-0.5 block text-[12.5px] leading-[19px] text-ink-dim">
                {canChange && 'Off takes it out of every chat within a minute.'}
                {!canChange && 'Only whoever turns tools on for the company can change this. Ask the owner.'}
              </span>
            </span>
            <Toggle checked={enabled} onChange={setEnabled} label={`${server.label} on for the company`} disabled={busy || !canChange} />
          </div>
          <div className="flex items-start justify-between gap-4 px-5 py-4">
            <span>
              <span className="block text-[13.5px] font-semibold text-ink">What the assistant may change</span>
              <span className="mt-0.5 block text-[12.5px] leading-[19px] text-ink-dim">
                Reading is always allowed. For changes, "Ask" shows the person an Approve card in their chat first.
              </span>
            </span>
            <Select
              aria-label={`What the assistant may change in ${server.label}`}
              className="w-[210px]"
              value={server.write_level}
              disabled={busy || !canSetLevel}
              onChange={(e) => setLevel(e.target.value as McpWriteLevel)}
            >
              <option value="approval">{WRITE_LABEL.approval}</option>
              <option value="allowed">{WRITE_LABEL.allowed}</option>
              <option value="off">{WRITE_LABEL.off}</option>
              <option value="person">{WRITE_LABEL.person}</option>
            </Select>
          </div>
          <div className="px-5 py-4">
            <div className="text-[13.5px] font-semibold text-ink">Connection</div>
            <div className="mt-0.5 text-[12.5px] leading-[19px] text-ink-dim">
              {connectionLabel(server)} · {AUTH_LABEL[server.auth]}{server.header ? ` (${server.header})` : ''} · {server.host}
            </div>
            {server.connected && server.connected_by && (
              <div className="mt-0.5 text-[12px] text-ink-soft">Connected by {server.connected_by}</div>
            )}
            {canChange && server.auth === 'oauth' && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Action disabled={busy || signingIn} reason="Waiting for the provider" onClick={signIn}>{server.connected ? 'Sign in again' : 'Sign in'}</Action>
                {server.connected && <Action variant="outline" disabled={busy} onClick={disconnect}>Disconnect</Action>}
                <span className="text-[12px] text-ink-dim">Opens the provider&apos;s own screen. Approve there and this row updates.</span>
              </div>
            )}
            {canChange && server.auth !== 'none' && server.auth !== 'oauth' && (
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <Field label={server.connected ? 'Replace the token' : 'Token or key'} className="min-w-[240px] flex-1">
                  <Input name="mcp-token" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Paste it here" autoComplete="new-password" />
                </Field>
                <Action disabled={busy || !secret.trim()} onClick={connect}>{server.connected ? 'Replace' : 'Connect'}</Action>
                {server.connected && <Action variant="outline" disabled={busy} onClick={disconnect}>Disconnect</Action>}
              </div>
            )}
          </div>
        </Divided>
      </Panel>
      {access}
    </McpDialog>
      {removing && (
        <RemoveMcpConfirm
          scope="company"
          label={server.label}
          auth={server.auth}
          connected={server.connected}
          busy={busy}
          onCancel={() => setRemoving(false)}
          onConfirm={confirmRemove}
        />
      )}
    </>
  );
};

const AddMcpDialog: React.FC<{ onClose: () => void; onAdded: (key: string) => void }> = ({ onClose, onAdded }) => {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [auth, setAuth] = useState<McpAuth>('bearer');
  const [header, setHeader] = useState('X-API-Key');
  const [secret, setSecret] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  const [touched, setTouched] = useState<{ label?: boolean; url?: boolean }>({});
  const [tried, setTried] = useState(false);
  const errors: { label?: string; url?: string } = {};
  if (!label.trim()) errors.label = 'Give it a name.';
  if (!url.trim()) errors.url = 'Give its address. It starts with https://';
  if (url.trim() && !/^https:\/\//.test(url.trim())) errors.url = 'The address must start with https://';
  const shown = (f: 'label' | 'url') => {
    if (tried || touched[f]) return errors[f];
    return undefined;
  };

  const save = async () => {
    if (Object.keys(errors).length > 0) {
      setTried(true);
      return;
    }
    setBusy(true);
    try {
      const { data } = await aaiApi.addMcp({
        label: label.trim(),
        url: url.trim(),
        auth,
        header: auth === 'header' ? header.trim() : undefined,
        description: description.trim(),
        secret: auth === 'bearer' || auth === 'header' ? secret.trim() || undefined : undefined,
      });
      toast.success(`${data.label} is in your catalog.`);
      if (data.warning) toast.warn(data.warning);
      onAdded(data.key);
    } catch (err) {
      toast.error(detailOf(err, 'Could not add that server.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <McpDialog
      onClose={onClose}
      kicker={<Kicker>Add an MCP server</Kicker>}
      title="A tool reached through its MCP server"
      sub="Its address and how it signs in. Everyone at the company can use it. The token is stored encrypted and only goes into your team's workspace sessions."
      footer={(
        <div className="flex justify-end gap-2">
          <Action variant="quiet" onClick={onClose}>Cancel</Action>
          <Action disabled={busy} reason="Adding" onClick={save}>Add</Action>
        </div>
      )}
    >
      <FieldGrid>
        <Field label="Name" htmlFor="mcp-name" marker="required" error={shown('label')} hint="What people will see, e.g. DeepWiki.">
          <Input id="mcp-name" name="mcp-name" value={label} onChange={(e) => setLabel(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, label: true }))} placeholder="DeepWiki" autoComplete="off" />
        </Field>
        <Field label="Address" htmlFor="mcp-url" marker="required" error={shown('url')} hint="The server's MCP address, starting with https://">
          <Input id="mcp-url" name="mcp-address" type="url" value={url} onChange={(e) => setUrl(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, url: true }))} placeholder="https://mcp.deepwiki.com/mcp" autoComplete="off" />
        </Field>
        <Field label="Sign-in" htmlFor="mcp-auth">
          <Select id="mcp-auth" value={auth} onChange={(e) => setAuth(e.target.value as McpAuth)}>
            <option value="bearer">{AUTH_LABEL.bearer}</option>
            <option value="header">{AUTH_LABEL.header}</option>
            <option value="oauth">{AUTH_LABEL.oauth}</option>
            <option value="none">{AUTH_LABEL.none}</option>
          </Select>
        </Field>
        {auth === 'header' && (
          <Field label="Header name" htmlFor="mcp-header"><Input id="mcp-header" value={header} onChange={(e) => setHeader(e.target.value)} /></Field>
        )}
        {auth === 'oauth' && (
          <p className="m-0 self-end text-[12.5px] leading-[19px] text-ink-dim">No token to paste. After adding, click Sign in on the server and approve on the provider&apos;s screen.</p>
        )}
        {auth !== 'none' && auth !== 'oauth' && (
          <Field label="Token or key" htmlFor="mcp-secret" marker="optional" hint="You can also add it later.">
            <Input id="mcp-secret" name="mcp-token" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="new-password" />
          </Field>
        )}
        <Field label="What it does" htmlFor="mcp-desc" className="sm:col-span-2" marker="optional" hint="One line shown under the server in the catalog.">
          <Input id="mcp-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Answers questions about GitHub repositories" />
        </Field>
      </FieldGrid>
    </McpDialog>
  );
};

export default McpServersPanel;
